import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, Mail, Loader2, AlertCircle, ShieldCheck } from "lucide-react";
import { adminLogin, adminMe, getAdminToken } from "../lib/adminApi";

const AdminLogin = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [checking, setChecking] = useState(true);

  // If a valid session already exists, send straight to dashboard.
  useEffect(() => {
    document.title = "RR Bros · Admin";
    let meta = document.querySelector('meta[name="robots"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "robots");
      document.head.appendChild(meta);
    }
    meta.setAttribute("content", "noindex,nofollow");
    (async () => {
      if (getAdminToken()) {
        try {
          await adminMe();
          navigate("/admin/dashboard", { replace: true });
          return;
        } catch {
          /* token invalid — fall through to login form */
        }
      }
      setChecking(false);
    })();
  }, [navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email.trim() || !password) {
      setError("Please enter your email and password.");
      return;
    }
    setLoading(true);
    try {
      await adminLogin(email.trim(), password);
      navigate("/admin/dashboard", { replace: true });
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === "string" ? d : "Invalid email or password.");
    } finally {
      setLoading(false);
    }
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-neutral-900 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-950 text-neutral-100 antialiased flex items-center justify-center px-4 py-10">
      {/* Intentionally no public header — hidden surface */}
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-orange-500/10 border border-orange-500/30 mb-3">
            <ShieldCheck className="w-6 h-6 text-orange-400" />
          </div>
          <h1 className="text-2xl font-black tracking-tight">
            RR Bros · Admin
          </h1>
          <p className="mt-1 text-xs text-neutral-400">
            Authorised personnel only
          </p>
        </div>

        <form
          onSubmit={onSubmit}
          noValidate
          data-testid="admin-login-form"
          className="bg-neutral-900 border border-neutral-800 rounded-2xl p-5 sm:p-6 space-y-4 shadow-2xl"
        >
          {error && (
            <div
              role="alert"
              data-testid="admin-login-error"
              className="flex items-start gap-2 bg-red-500/10 border border-red-500/30 text-red-300 rounded-xl px-3 py-2 text-sm"
            >
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <div>
            <label className="text-[11px] font-bold uppercase tracking-wider text-neutral-400 mb-1.5 block">
              Email
            </label>
            <div className="relative">
              <Mail className="w-4 h-4 text-orange-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                type="email"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@example.com"
                data-testid="admin-email-input"
                className="w-full h-12 pl-10 pr-3 rounded-xl bg-neutral-950 border border-neutral-800 text-sm font-semibold text-neutral-100 outline-none focus:ring-2 focus:ring-orange-500/60 focus:border-transparent"
              />
            </div>
          </div>

          <div>
            <label className="text-[11px] font-bold uppercase tracking-wider text-neutral-400 mb-1.5 block">
              Password
            </label>
            <div className="relative">
              <Lock className="w-4 h-4 text-orange-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                data-testid="admin-password-input"
                className="w-full h-12 pl-10 pr-3 rounded-xl bg-neutral-950 border border-neutral-800 text-sm font-semibold text-neutral-100 outline-none focus:ring-2 focus:ring-orange-500/60 focus:border-transparent"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-submit"
            className="w-full h-12 rounded-xl bg-orange-500 hover:bg-orange-600 disabled:bg-neutral-700 text-white font-black text-sm uppercase tracking-wider inline-flex items-center justify-center gap-2 transition-colors"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Lock className="w-4 h-4" />
            )}
            Sign In
          </button>
        </form>

        <p className="text-center text-[11px] text-neutral-500 mt-5">
          This area is private and not linked from the public website.
        </p>
      </div>
    </div>
  );
};

export default AdminLogin;
